import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import "./SectionAbout.css";

import {
  FiPenTool,
  FiMonitor,
  FiTarget,
  FiBriefcase,
} from "react-icons/fi";

// ============================================
// Services Data
// ============================================

const services = [
  {
    id: "01",
    title: "Digital Marketing",
    description:
      "Driving measurable growth through data-driven strategies, targeted campaigns, and impactful digital experiences that convert audiences into loyal customers.",
    icon: <FiPenTool />,
    popular: false,
  },
  {
    id: "02",
    title: "Video Editing",
    description:
      "Crafting compelling visual stories with seamless editing, cinematic effects, and engaging content that captures attention and leaves a lasting impression.",
    icon: <FiMonitor />,
    popular: true,
  },
  // {
  //   id: "03",
  //   title: "Website Development",
  //   description:
  //     "Building fast, responsive, and scalable websites that combine modern design with seamless functionality to deliver exceptional user experiences.",
  //   icon: <FiTarget />,
  //   popular: false,
  // },
  {
    id: "03",
    title: "Video Shoots",
    description:
      "Producing high-quality, professional video shoots that bring your brand, products, and stories to life with creativity and precision.",
    icon: <FiBriefcase />,
    popular: false,
  },
];

// ============================================
// Component
// ============================================

export default function SectionAbout() {
  // Active row
  const [activeIndex, setActiveIndex] = useState(1);

  // Section reference
  const sectionRef = useRef(null);

  // Heading reference
  const headingRef = useRef(null);

  // Divider reference
  const dividerRef = useRef(null);

  // Service rows
  const rowsRef = useRef([]);

  // Description refs
  const contentRef = useRef([]);

  // Store row refs
  const addRowRef = (el, index) => {
    if (el) rowsRef.current[index] = el;
  };

  // Store content refs
  const addContentRef = (el, index) => {
    if (el) contentRef.current[index] = el;
  };
    // ============================================
  // Framer Motion Variants
  // ============================================

  // Section fade in
  const sectionVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: 0.18,
      },
    },
  };

  // Heading animation
  const headingVariants = {
    hidden: {
      opacity: 0,
      y: 80,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Divider animation
  const dividerVariants = {
    hidden: {
      scaleX: 0,
      opacity: 0,
    },
    visible: {
      scaleX: 1,
      opacity: 1,
      transition: {
        duration: 1,
        ease: "easeInOut",
      },
    },
  };

  // Service Row Animation
  const rowVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.75,
        ease: "easeOut",
      },
    },
  };

  // Icon Animation
  const iconVariants = {
    rest: {
      rotate: 0,
      scale: 1,
    },
    hover: {
      rotate: 8,
      scale: 1.15,
      transition: {
        duration: 0.3,
      },
    },
  };

  // Number Animation
  const numberVariants = {
    rest: {
      opacity: 0.6,
      x: 0,
    },
    hover: {
      opacity: 1,
      x: 10,
      transition: {
        duration: 0.25,
      },
    },
  };

  // Title Animation
  const titleVariants = {
    rest: {
      x: 0,
    },
    hover: {
      x: 12,
      transition: {
        duration: 0.3,
      },
    },
  };

  // Description Animation
  const descriptionVariants = {
    hidden: {
      opacity: 0,
      y: 15,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  // Active Row Animation
  const activeRowVariants = {
    inactive: {
      backgroundColor: "rgba(255,255,255,0)",
      borderColor: "rgba(255,255,255,0.08)",
      transition: {
        duration: 0.3,
      },
    },
    active: {
      backgroundColor: "rgba(255,255,255,0.03)",
      borderColor: "rgba(255,255,255,0.15)",
      transition: {
        duration: 0.3,
      },
    },
  };

  // Popular Badge Animation
  const badgeVariants = {
    hidden: {
      opacity: 0,
      scale: 0.7,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 0.25,
        duration: 0.35,
      },
    },
  };

  // Hover Lift
  const hoverLift = {
    y: -6,
    transition: {
      duration: 0.25,
      ease: "easeOut",
    },
  };

  // Card Tap
  const tapAnimation = {
    scale: 0.98,
  };
    // ============================================
  // Event Handlers & Interaction Logic
  // ============================================

  // Set active row on hover (Desktop)
  const handleMouseEnter = (index) => {
    setActiveIndex(index);
  };

  // Keep current active row when mouse leaves
  const handleMouseLeave = () => {
    // Intentionally left blank.
    // If you want the first row to become active again,
    // replace this with:
    // setActiveIndex(0);
  };

  // Click support (Desktop + Mobile)
  const handleClick = (index) => {
    setActiveIndex(index);
  };

  // Keyboard accessibility
  const handleKeyDown = (event, index) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setActiveIndex(index);
    }
  };

  // ============================================
  // Motion Props Helpers
  // ============================================

  const getRowAnimation = (index) => ({
    initial: "hidden",
    whileInView: "visible",
    viewport: {
      once: true,
      amount: 0.3,
    },
    variants: rowVariants,
    animate: activeIndex === index ? "active" : "inactive",
  });

  const getHoverState = (index) => ({
    initial: "rest",
    whileHover: "hover",
    animate: activeIndex === index ? "hover" : "rest",
  });

  // ============================================
  // Active Description
  // (Useful if you later want only one description
  // to be shown at a time.)
  // ============================================

  const activeService = services[activeIndex];

  // ============================================
  // Utility
  // ============================================

  const isActive = (index) => activeIndex === index;
    // ============================================
  // JSX
  // ============================================

  return (
    <motion.section id="about"
      ref={sectionRef}
      className="about-section"
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      {/* =========================
          Heading
      ========================== */}

      <motion.div
        className="about-heading"
        ref={headingRef}
        variants={headingVariants}
      >
        <h2>
          It's so challenging to find a good
          <br />
          team to do great things.
          <br />
          But we can provide you
          <br />
          the best one.
        </h2>
      </motion.div>

      {/* =========================
          Divider
      ========================== */}

      <motion.div
        className="divider"
        ref={dividerRef}
        variants={dividerVariants}
      />

      {/* =========================
          Services
      ========================== */}

      <div className="services-list">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            ref={(el) => addRowRef(el, index)}
            className={`service-row ${
              isActive(index) ? "active" : ""
            }`}
            variants={rowVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            whileHover={hoverLift}
            whileTap={tapAnimation}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleClick(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            tabIndex={0}
            role="button"
          >
            {/* =========================
                Left
            ========================== */}

            <div className="service-left">

              <motion.div
                className="service-number"
                variants={numberVariants}
                initial="rest"
                animate={isActive(index) ? "hover" : "rest"}
              >
                {service.id}
              </motion.div>

              <motion.div
                className="service-icon"
                variants={iconVariants}
                initial="rest"
                animate={isActive(index) ? "hover" : "rest"}
              >
                {service.icon}
              </motion.div>

              <div className="service-info">

                <div className="title-wrapper">

                  <motion.h3
                    className="service-title"
                    variants={titleVariants}
                    initial="rest"
                    animate={isActive(index) ? "hover" : "rest"}
                  >
                    {service.title}
                  </motion.h3>

                  {service.popular && (
                    <motion.span
                      className="popular-badge"
                      variants={badgeVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      POPULAR
                    </motion.span>
                  )}

                </div>

              </div>

            </div>

            {/* =========================
                Right
            ========================== */}

            <div
              className="service-right"
              ref={(el) => addContentRef(el, index)}
            >
              <motion.p
                className="service-description"
                variants={descriptionVariants}
                initial="hidden"
                animate="visible"
              >
                {service.description}
              </motion.p>
            </div>

            {/* Hover Glow */}

            <div className="row-glow"></div>

          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}